/**
 * Plan entitlements (FR-BIL-01). Limits are product rules and live in code; prices are set by
 * the platform admin (FR-ADM-03) because they change without a release.
 */
export const PLAN_IDS = ["free", "starter", "business", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export const FEATURES = [
  "vat",
  "offline",
  "multi_branch",
  "custom_domain",
  "api",
  "couriers",
  "advanced_reports",
] as const;
export type Feature = (typeof FEATURES)[number];
const FEATURE_SET: ReadonlySet<string> = new Set(FEATURES);
export const isFeature = (value: string): value is Feature => FEATURE_SET.has(value);

export interface PlanDef {
  id: PlanId;
  name: { en: string; bn: string };
  /** null = unlimited. */
  limits: { branches: number | null; users: number | null; items: number | null };
  /** SMS credits included each paid month. */
  smsPerMonth: number;
  features: readonly Feature[];
  /** Plans a tenant can buy online; enterprise is contracted. */
  selfServe: boolean;
}

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: "free",
    name: { en: "Free", bn: "ফ্রি" },
    limits: { branches: 1, users: 2, items: 300 },
    smsPerMonth: 0,
    features: [],
    selfServe: false,
  },
  starter: {
    id: "starter",
    name: { en: "Starter", bn: "স্টার্টার" },
    limits: { branches: 1, users: 5, items: null },
    smsPerMonth: 100,
    features: ["vat", "offline"],
    selfServe: true,
  },
  business: {
    id: "business",
    name: { en: "Business", bn: "বিজনেস" },
    limits: { branches: 3, users: 15, items: null },
    smsPerMonth: 300,
    features: [
      "vat",
      "offline",
      "multi_branch",
      "custom_domain",
      "api",
      "couriers",
      "advanced_reports",
    ],
    selfServe: true,
  },
  enterprise: {
    id: "enterprise",
    name: { en: "Enterprise", bn: "এন্টারপ্রাইজ" },
    limits: { branches: null, users: null, items: null },
    smsPerMonth: 1000,
    features: FEATURES,
    selfServe: false,
  },
};

/** Days a lapsed paid plan keeps working before falling back to Free limits (FR-BIL-04). */
export const GRACE_DAYS = 7;

export interface SubscriptionState {
  plan: PlanId;
  status: "active" | "grace" | "lapsed" | "cancelled";
  currentPeriodEnd: number | null;
}

/** The plan whose limits apply right now: paid plans fall back to Free once the grace period ends. */
export function effectivePlan(
  sub: SubscriptionState,
  now: number,
): { plan: PlanId; status: "active" | "grace" | "lapsed"; graceEndsAt: number | null } {
  if (sub.plan === "free" || sub.currentPeriodEnd === null)
    return { plan: sub.plan, status: "active", graceEndsAt: null };
  if (now < sub.currentPeriodEnd) return { plan: sub.plan, status: "active", graceEndsAt: null };
  // A plan cancelled at the end of its period stops then, without a grace period.
  if (sub.status === "cancelled") return { plan: "free", status: "lapsed", graceEndsAt: null };
  const graceEndsAt = sub.currentPeriodEnd + GRACE_DAYS * 86_400_000;
  if (now < graceEndsAt) return { plan: sub.plan, status: "grace", graceEndsAt };
  return { plan: "free", status: "lapsed", graceEndsAt };
}

export function hasFeature(plan: PlanId, feature: Feature): boolean {
  return PLANS[plan].features.includes(feature);
}

/**
 * SMS segments for billing (FR-NTF-04). Bangla text is sent as UCS-2: 70 characters fit one
 * message, 67 per part when split. Plain GSM-7 text: 160 and 153.
 */
export function smsSegments(body: string): number {
  const ucs2 = [...body].some((ch) => (ch.codePointAt(0) ?? 0) > 0x7f);
  const length = [...body].length;
  const single = ucs2 ? 70 : 160;
  const part = ucs2 ? 67 : 153;
  if (length === 0) return 0;
  return length <= single ? 1 : Math.ceil(length / part);
}
